import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { cybervolunteerApi } from "../../api/cybervolunteerApi";
import Header from "../../components/Header";
import type { TaskQuestionType } from "./AdminEditTaskPageTypes";

const AdminEditTaskPage = () => {
  const { subtaskId } = useParams();
  const [subtaskTitle, setSubtaskTitle] = useState<string>("");
  const [subTaskDescription, setSubtaskDescription] = useState<string>("");
  const [subtaskText, setSubtaskText] = useState<string>("");
  const [subtaskQuestions, setSubtaskQuestions] = useState<Array<TaskQuestionType>>([]);
  const [subtaskIsHero, setSubtaskIsHero] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const remove_subtask = (subtask_id: number) => {
    cybervolunteerApi
      .removeSubtask({
        subtask_id: subtask_id,
      })
      .then((_response) => {
        window.location.href = "/lk";
      })
      .catch((error) => {
        console.error(error);

        /* istanbul ignore else -- non-405 errors only log */
        if (error.response?.status === 405) {
          window.location.href = "/";
        }
      });
  };

  const save_subtask = () => {
    let idNum = NaN;
    /* istanbul ignore else -- route always supplies :subtaskId */
    if (subtaskId) {
      idNum = Number(subtaskId);
    } else {
      /* istanbul ignore next -- route always includes :subtaskId */
      idNum = NaN;
    }
    if (Number.isNaN(idNum)) return;

    cybervolunteerApi
      .saveSubtask({
        subtask_id: idNum,
        title: subtaskTitle,
        description: subTaskDescription,
        text_md: subtaskText,
        is_hero: subtaskIsHero,
        questions: subtaskQuestions.map((q) => ({
          id: q.id,
          question_type: q.question_type,
          content: q.content,
        })),
      })
      .then((_response) => {
        window.location.href = "/task_list_admin";
      })
      .catch((error) => {
        console.error(error);

        /* istanbul ignore else -- non-405 errors only log */
        if (error.response?.status === 405) {
          window.location.href = "/";
        }
      });
  };

  const get_data = useCallback(() => {
    cybervolunteerApi
      .getSubtask(subtaskId?.toString() ?? "")
      .then((response) => {
        if (response.data) {
          setSubtaskTitle(response.data.title);
          setSubtaskDescription(response.data.description);
          setSubtaskText(response.data.text_md);
          setSubtaskQuestions(response.data.questions ?? []);
          setSubtaskIsHero(Boolean(response.data.is_hero));
          console.log(response.data.questions);
        }
      })
      .catch((error) => {
        console.error(error);

        /* istanbul ignore else -- non-405 errors only log */
        if (error.response?.status === 405) {
          window.location.href = "/";
        }
      });
  }, [subtaskId]);

  useEffect(() => {
    get_data();
  }, [get_data]);

  const updateQuestion = (
    questionId: number,
    updater: (q: TaskQuestionType) => TaskQuestionType
  ) => {
    setSubtaskQuestions((prev) => prev.map((q) => (q.id === questionId ? updater(q) : q)));
  };

  const removeQuestionById = (questionId: number) => {
    setSubtaskQuestions((prev) => prev.filter((q) => q.id !== questionId));
  };

  const addQuestion = () => {
    setSubtaskQuestions((prev) => {
      const minTemp = prev.reduce((m, q) => (q.id < 0 ? Math.min(m, q.id) : m), 0);
      const nextId = minTemp === 0 ? -1 : minTemp - 1;
      return [
        ...prev,
        {
          id: nextId,
          question_type: "TX",
          content: { title: "", options: undefined },
        },
      ];
    });
  };

  const addQuestionOption = (questionId: number) => {
    updateQuestion(questionId, (q) => {
      /* istanbul ignore next -- options are initialized before + is shown; guard is defensive */
      const opts = q.content.options ?? [];
      const nextId = opts.length === 0 ? 0 : Math.max(...opts.map((o) => o.id)) + 1;
      return {
        ...q,
        content: {
          ...q.content,
          options: [...opts, { id: nextId, text: "" }],
        },
      };
    });
  };

  const get_csv = (task_number: number, task_title: string) => {
    cybervolunteerApi.getCsvBlob(task_number).then((response) => {
      const href = URL.createObjectURL(response.data);

      // create "a" HTML element with href to file & click
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", task_title + ".csv"); //or any other extension
      document.body.appendChild(link);
      link.click();

      // clean up "a" element & remove ObjectURL
      document.body.removeChild(link);
      URL.revokeObjectURL(href);
    });
  };
  return (
    <div className="main-body border-l-orange-100 md:pb-10">
      <Header />
      <main className="w-full">
        <div className="text-black">
          <div className="relative w-full container flex justify-between mx-auto flex-col">
            <button
              type="button"
              data-testid="back-to-task-list-admin"
              className="m-3 self-start rounded-lg border-2 border-slate-400 px-4 py-2 text-lg text-slate-800 hover:bg-slate-100"
              onClick={() => {
                window.location.href = "/task_list_admin";
              }}
            >
              {"< Back"}
            </button>
            <h3 className="m-3 text-3xl">Номер задачи: {subtaskId}</h3>
            <div className="flex">
              <h3 className="m-3 text-3xl">Имя задачи</h3>
              <input
                type="text"
                value={subtaskTitle}
                className="border-2 m-3 rounded-lg"
                onChange={(e) => setSubtaskTitle(e.target.value)}
              ></input>
            </div>
            <div className="flex">
              <h3 className="m-3 text-3xl">Описание задачи</h3>
              <input
                type="text"
                value={subTaskDescription}
                className="border-2 m-3 rounded-lg"
                onChange={(e) => setSubtaskDescription(e.target.value)}
              ></input>
            </div>
            <div className="flex flex-col">
              <h3 className="m-3 text-3xl">Текст задачи</h3>
              <textarea
                className="border-2 m-3 rounded-lg w-auto text-black text-2xl h-80"
                value={subtaskText}
                onChange={(e) => setSubtaskText(e.target.value)}
              />
            </div>
            <div className="flex items-center m-3">
              <input
                id="subtask-is-hero"
                data-testid="subtask-is-hero"
                type="checkbox"
                className="w-5 h-5 mr-3"
                checked={subtaskIsHero}
                onChange={(e) => setSubtaskIsHero(e.target.checked)}
              />
              <label htmlFor="subtask-is-hero" className="text-2xl">
                Hero-задача (без ответов, всегда раскрыта)
              </label>
            </div>
            <div className="bg-red-300 p-3 rounded-lg shadow-lg">
              <p>при изменении задачи все отправленные ответы будут удалены!</p>
              <p>
                если ты боишься потерять данные — выгрузи ответы в
                <button
                  className="text-blue-800 px-1"
                  onClick={() => {
                    let subtaskNum = 0;
                    /* istanbul ignore else -- route always supplies :subtaskId */
                    if (subtaskId) {
                      subtaskNum = Number(subtaskId);
                    } else {
                      /* istanbul ignore next -- route always includes :subtaskId */
                      subtaskNum = 0;
                    }
                    get_csv(subtaskNum, subtaskTitle);
                  }}
                >
                  CSV
                </button>
              </p>
            </div>

            <div className="mt-3 shadow-[0_0_10px_rgba(0,0,0,0.25)] rounded-md p-3 text-black">
              <div className="mb-2">
                <button
                  type="button"
                  data-testid="add-question"
                  onClick={addQuestion}
                  className="text-white bg-slate-600 p-2 px-6 rounded-lg"
                >
                  Добавить вопрос
                </button>
              </div>
              <table className="shadow-sm rounded-md w-full border-separate border-spacing-2">
                <thead className="py-3 rounded-lg">
                  <tr className="table-row">
                    <th className="text-left">Название</th>
                    <th className="text-left">Тип</th>
                    <th className="text-left">Контент</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {subtaskQuestions.map((subtask_question) => {
                    return (
                      <tr key={subtask_question.id} className="table-row text-black">
                        <td className="w-3/12 align-top">
                          <input
                            className="w-full border-2 p-2 mt-2 rounded-md"
                            type="text"
                            value={subtask_question.content.title}
                            onChange={(e) =>
                              updateQuestion(subtask_question.id, (q) => ({
                                ...q,
                                content: { ...q.content, title: e.target.value },
                              }))
                            }
                          />
                        </td>
                        <td className="w-2/12 align-top">
                          <select
                            className="p-2 mt-3 w-full"
                            value={subtask_question.question_type}
                            onChange={(e) => {
                              const v = e.target.value;
                              updateQuestion(subtask_question.id, (q) => ({
                                ...q,
                                question_type: v,
                                content:
                                  v === "TX"
                                    ? { title: q.content.title, options: undefined }
                                    : {
                                        title: q.content.title,
                                        options:
                                          q.content.options && q.content.options.length > 0
                                            ? q.content.options
                                            : [],
                                      },
                              }));
                            }}
                          >
                            <option value="TX">текст</option>
                            <option value="CH">чекбокс</option>
                            <option value="RD">радио</option>
                          </select>
                        </td>
                        <td className="w-6/12">
                          {subtask_question.question_type === "TX" ? (
                            <></>
                          ) : subtask_question.content.options ? (
                            <div className="border border-gray-200 p-3 rounded-md">
                              {subtask_question.content.options.map((subtask_question_option) => {
                                return (
                                  <div key={subtask_question_option.id}>
                                    <input
                                      className="w-full border-2 p-2 my-1 rounded-md"
                                      type="text"
                                      value={subtask_question_option.text}
                                      onChange={(e) =>
                                        updateQuestion(subtask_question.id, (q) => {
                                          /* istanbul ignore next -- editors mount only when options exist */
                                          const base = q.content.options || [];
                                          return {
                                            ...q,
                                            content: {
                                              ...q.content,
                                              options: base.map((o) =>
                                                o.id === subtask_question_option.id
                                                  ? { ...o, text: e.target.value }
                                                  : o
                                              ),
                                            },
                                          };
                                        })
                                      }
                                    />
                                  </div>
                                );
                              })}
                              <button
                                type="button"
                                className="mx-2 rounded-lg p-1 px-3 bg-green-300"
                                data-testid="add-question-option"
                                onClick={() => addQuestionOption(subtask_question.id)}
                              >
                                +
                              </button>
                            </div>
                          ) : (
                            <></>
                          )}
                        </td>
                        <td className="w-1/12">
                          <button
                            type="button"
                            className="mx-2 rounded-lg p-1 px-3 bg-red-300"
                            data-testid="remove-question"
                            onClick={() => removeQuestionById(subtask_question.id)}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="w-full flex">
              <button
                type="button"
                data-testid="save-task"
                onClick={save_subtask}
                className="text-white bg-slate-600 m-3 p-2 px-12 rounded-lg md:max-w-72 max-w-full text-center"
              >
                Сохранить задачу
              </button>
              <button
                type="button"
                data-testid="delete-task"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-white bg-red-600 m-3 p-2 px-12 rounded-lg md:max-w-72 max-w-full text-center"
              >
                Удалить задачу
              </button>
            </div>

            {showDeleteConfirm ? (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-task-confirm-title"
                onClick={(e) => {
                  if (e.target === e.currentTarget) setShowDeleteConfirm(false);
                }}
              >
                <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl text-black">
                  <h3 id="delete-task-confirm-title" className="text-xl font-semibold mb-2">
                    Удалить задачу?
                  </h3>
                  <p className="text-gray-700 mb-6">
                    Задача будет удалена без возможности восстановления.
                  </p>
                  <div className="flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      data-testid="delete-task-cancel"
                      className="rounded-lg border-2 border-gray-300 px-6 py-2 font-medium hover:bg-gray-50"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      data-testid="delete-task-confirm"
                      className="rounded-lg bg-red-600 px-6 py-2 font-medium text-white hover:bg-red-700"
                      onClick={() => {
                        let idNum = NaN;
                        /* istanbul ignore else -- route always supplies :subtaskId */
                        if (subtaskId) {
                          idNum = Number(subtaskId);
                        } else {
                          /* istanbul ignore next -- route always includes :subtaskId */
                          idNum = NaN;
                        }
                        setShowDeleteConfirm(false);
                        if (!Number.isNaN(idNum)) {
                          remove_subtask(idNum);
                        }
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditTaskPage;
